import { BookedHockeyLesson } from '@types';
import { PDFViewerModal } from '_components/atoms';
import UserService from '_services/UserService';
import HelperMethods from '_utils/HelperMethods';
import React, { useEffect, useState } from 'react';
import Barcode from 'react-barcode';

const MyBookedHockeyLessons: React.FC = () => {
  const [lessons, setLessons] = useState<BookedHockeyLesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [pdfViewerModal, setPdfViewerModal] = useState<{
    isOpen: boolean;
    url: string;
    title: string;
  }>({
    isOpen: false,
    url: '',
    title: '',
  });

  const fetchLessons = async () => {
    try {
      setLoading(true);

      const responseData =
        (await UserService.getBookedHockeyLessons()) as unknown as BookedHockeyLesson[];

      if (responseData) {
        setLessons(responseData);
      }
    } catch (error) {
      console.error('Error fetching lessons:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLessons();
  }, []);

  const openPdfViewer = (url: string, title: string) => {
    setPdfViewerModal({
      isOpen: true,
      url,
      title,
    });
  };

  const closePdfViewer = () => {
    setPdfViewerModal({
      isOpen: false,
      url: '',
      title: '',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            My Booked Hockey Lessons
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            View your booked hockey lessons
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <div className="text-sm text-gray-600">
            Total Lessons:{' '}
            <span className="font-semibold">{lessons.length}</span>
          </div>
        </div>
      </div>

      {/* Lessons Table - Desktop */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lesson Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Participant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {lessons.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    {loading ? 'Loading...' : 'No lessons found'}
                  </td>
                </tr>
              ) : (
                lessons.map(lesson => (
                  <tr key={lesson.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col items-center">
                        <div className="mb-2">
                          <Barcode
                            value={lesson.barcodeId}
                            width={1}
                            height={50}
                            fontSize={10}
                            format="CODE128"
                          />
                        </div>
                        <div className="text-xs text-gray-500 text-center">
                          {lesson.barcodeId}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {lesson.title}
                      </div>
                      <div className="text-sm text-gray-500">
                        Lesson #{lesson.hockeyLessonId}
                      </div>
                      <div className="text-xs text-gray-400">
                        Receipt: {lesson.receiptNumber}
                      </div>
                      <div className="text-xs text-gray-400">
                        Booked:{' '}
                        {HelperMethods.formatDateTimeLocal(lesson.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {lesson.participantFirstName}{' '}
                        {lesson.participantLastName}
                      </div>
                      <div className="text-sm text-gray-500">
                        DOB:{' '}
                        {HelperMethods.formatDate(
                          lesson.participantDateOfBirth,
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {lesson.customerName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {lesson.customerEmail}
                      </div>
                      <div className="text-sm text-gray-500">
                        {lesson.customerPhone}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          lesson.redeemed
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {lesson.redeemed ? 'Redeemed' : 'Not Redeemed'}
                      </span>
                      {lesson.redeemed && lesson.redeemedBy && (
                        <div className="text-xs text-gray-500 mt-1">
                          by {lesson.redeemedBy}
                        </div>
                      )}
                      {lesson.redeemed && lesson.redeemedAt && (
                        <div className="text-xs text-gray-500 mt-1">
                          Redeemed on{' '}
                          {HelperMethods.formatDateTimeLocal(lesson.redeemedAt)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2 justify-between">
                        {lesson.waiverUrl ? (
                          <button
                            onClick={() =>
                              openPdfViewer(lesson.waiverUrl!, 'View Waiver')
                            }
                            className="text-blue-600 hover:text-blue-900"
                          >
                            View Waiver
                          </button>
                        ) : (
                          <div className="text-gray-500">No Waiver</div>
                        )}
                        {lesson.receiptUrl && (
                          <button
                            onClick={() =>
                              openPdfViewer(lesson.receiptUrl!, 'View Receipt')
                            }
                            className="text-blue-600 hover:text-blue-900"
                          >
                            View Receipt
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lessons Cards - Mobile */}
      <div className="md:hidden space-y-4">
        {lessons.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
            {loading ? 'Loading...' : 'No lessons found'}
          </div>
        ) : (
          lessons.map(lesson => (
            <div
              key={lesson.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
            >
              {/* Lesson Info */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {lesson.title}
                  </h3>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      lesson.redeemed
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {lesson.redeemed ? 'Redeemed' : 'Not Redeemed'}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  Lesson #{lesson.hockeyLessonId}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Receipt: {lesson.receiptNumber}
                </div>
                <div className="text-xs text-gray-400">
                  Booked: {HelperMethods.formatDateTimeLocal(lesson.createdAt)}
                </div>
              </div>

              {/* Participant Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Participant
                </h4>
                <div className="text-sm text-gray-600">
                  <div className="font-medium">
                    {lesson.participantFirstName} {lesson.participantLastName}
                  </div>
                  <div>
                    DOB:{' '}
                    {HelperMethods.formatDate(lesson.participantDateOfBirth)}
                  </div>
                </div>
              </div>

              {/* Customer Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Customer
                </h4>
                <div className="text-sm text-gray-600">
                  <div className="font-medium">{lesson.customerName}</div>
                  <div>{lesson.customerEmail}</div>
                  <div>{lesson.customerPhone}</div>
                </div>
              </div>

              {/* Barcode Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <div className="flex flex-col items-center">
                  <div className="mb-2">
                    <Barcode
                      value={lesson.barcodeId}
                      width={1}
                      height={60}
                      fontSize={12}
                      format="CODE128"
                    />
                  </div>
                  <div className="text-xs text-gray-500 text-center">
                    {lesson.barcodeId}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                {lesson.waiverUrl && (
                  <button
                    onClick={() =>
                      openPdfViewer(lesson.waiverUrl!, 'View Waiver')
                    }
                    className="inline-flex items-center px-3 py-2 bg-blue-100 text-blue-700 text-sm rounded-md hover:bg-blue-200 transition-colors"
                  >
                    View Waiver
                  </button>
                )}
                {lesson.receiptUrl && (
                  <button
                    onClick={() =>
                      openPdfViewer(lesson.receiptUrl!, 'View Receipt')
                    }
                    className="inline-flex items-center px-3 py-2 bg-blue-100 text-blue-700 text-sm rounded-md hover:bg-blue-200 transition-colors"
                  >
                    View Receipt
                  </button>
                )}
              </div>

              {/* Redeemed By Info */}
              {lesson.redeemed && lesson.redeemedBy && (
                <div className="mt-3 text-xs text-gray-500">
                  Redeemed by: {lesson.redeemedBy}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={pdfViewerModal.isOpen}
        onClose={closePdfViewer}
        url={pdfViewerModal.url}
        title={pdfViewerModal.title}
      />
    </div>
  );
};

export default MyBookedHockeyLessons;
